import { types } from "./types";

/**
 * @param {array} states
 * @param {array} questions
 * @param {array} orgResponses
 * @param {array} kpis
 * @param {object} loader
 * @param {object} kpi
 * @param {object} selectedState
 * @param {object} selectedOrg
 */
const INITIAL_STATE = {
    states: [], 
    questions: [], 
    orgResponses: [], 
    kpis: [], 
    kpi: null, 
    selectedState: null, 
    currentSection: null,  // questionnaire sections
    selectedOrg: "", 
    organisations: [],
    spsAndResponses: {},
    loader: {
        actions: []
    },
    provision: null,
    error: null,
    success: false,
    answerStatus: {}, // YDMS_SP_id -> 'saving' | 'saved' | 'error'
}

const YDMonitoringReducer = (state = INITIAL_STATE, action) => {
    const { loader } = state;
    const { payload } = action;
    
    switch (action.type) {

        case types.START_LOADING:
            return {
                ...state,
                loader: {
                    ...loader,
                    actions: [...new Set([...loader.actions, payload])], 
                }
            };

        case types.STOP_LOADING:
            return {
                ...state,
                loader: {
                    ...loader,
                    actions: loader.actions.filter(action => action !== payload),
                }
            };

        case types.GET_STATES_SUCCESS:
            return {
                ...state,
                states: action.payload,
                error: null
            };
        case types.GET_STATES_FAILURE:
            return {
                ...state,
                states: [],
                error: action.payload
            };

        case types.GET_QUESTIONNAIRE_SUCCESS:
            return {
                ...state,
                questions: action.payload,
                error: null
            };
        case types.GET_QUESTIONNAIRE_FAILURE:
            return {
                ...state,
                questions: [],
                error: action.payload
            };

        case types.GET_SPS_AND_RESPONSES_SUCCESS:
            return {
                ...state,
                spsAndResponses: action.payload,
                answerStatus: {},
                error: null
            };
        case types.GET_SPS_AND_RESPONSES_FAILURE:
            return {
                ...state,
                spsAndResponses: [],
                error: action.payload
            };

        case types.GET_ORG_RESPONSES_SUCCESS:
            return {
                ...state,
                orgResponses: action.payload,
                error: null
            };
        case types.GET_ORG_RESPONSES_FAILURE:
            return {
                ...state,
                orgResponses: [],
                error: action.payload
            };

        case types.SAVE_RESPONSE_SUCCESS:
            return {
                ...state,
                success: true,
                error: null,
                orgResponses: action.payload.orgResponses,
                spsAndResponses: action.payload.spsAndResponses,
                states: action.payload.states,
            };

        case types.SAVE_RESPONSE_FAILURE:
            return {
                ...state,
                error: action.payload,
                success: false,
            };

        case types.BULK_SAVE_RESPONSE_SUCCESS:
            return {
                ...state,
                bulkSuccess: true,
                error: null,
            };

        case types.BULK_SAVE_RESPONSE_FAILURE:
            return {
                ...state,
                bulkSuccess: false,
                error: action.payload,
            };

        case types.RESTORE_BULK_RESPONSE_STATUS:
            return {
                ...state,
                bulkSuccess: false,
            };

        case types.GET_ORGANISATIONS_SUCCESS:
            return {
                ...state,
                organisations: action.payload,
                error: null
            };

        case types.RESTORE_RESPONSE_STATUS:
            return {
                ...state,
                success: false,
            };

        case types.SET_STATE_REQUEST:
            return {
                ...state,
                selectedState: action.payload,
            };

        case types.SET_ORG_REQUEST:
            return {
                ...state,
                selectedOrg: action.payload,
                selectedState: null
            };

        case types.SET_CURRENT_SECTION:
            return {
                ...state,
                currentSection: action.payload,
            };

        case types.GET_PROVISION_SUCCESS:
            return {
                ...state,
                provision: action.payload,
            };

        case types.GET_PROVISION_FAILURE:
            return {
                ...state,
                provision: [],
            };

        case types.SAVE_ANSWER_REQUEST:
            return {
                ...state,
                answerStatus: { ...state.answerStatus, [action.payload.answer.YDMS_SP_id]: 'saving' },
            };

        case types.SAVE_ANSWER_FAILURE:
            return {
                ...state,
                answerStatus: { ...state.answerStatus, [action.payload.spId]: 'error' },
            };

        case types.SAVE_ANSWER_SUCCESS: {
            const { answer } = action.payload;
            const spId = answer.YDMS_SP_id;

            const newResponse = {
                surveyProtocolYDMSSPId: spId,
                organisationYDMSOrgId: answer.YDMS_Org_id,
                questionnaire_response: answer.response,
                weight_response: answer.weight ?? 0,
                kpi_id: answer.kpi,
            };

            // Upsert into orgResponses (match by surveyProtocolYDMSSPId).
            const exists = state.orgResponses.some(r => r.surveyProtocolYDMSSPId === spId);
            const orgResponses = exists
                ? state.orgResponses.map(r => r.surveyProtocolYDMSSPId === spId ? { ...r, ...newResponse } : r)
                : [...state.orgResponses, newResponse];

            // Write sp_response into the matching survey protocol.
            const protocols = state.spsAndResponses?.survey_protocols || [];
            const survey_protocols = protocols.map(sp =>
                sp.YDMS_SP_id === spId ? { ...sp, sp_response: { ...(sp.sp_response || {}), ...newResponse } } : sp
            );

            // Preserve legacy behaviour: kpi_1 answer updates the state's SAATM_membership.
            let states = state.states;
            if (answer.kpi === 'kpi_1') {
                states = state.states.map(st =>
                    st.YDMS_AU_id === answer.YDMS_Org_id ? { ...st, SAATM_membership: !!answer.response } : st
                );
            }

            return {
                ...state,
                orgResponses,
                spsAndResponses: { ...state.spsAndResponses, survey_protocols },
                states,
                answerStatus: { ...state.answerStatus, [spId]: 'saved' },
                error: null,
            };
        }

        default:
            return state;
    }
}

export default YDMonitoringReducer;